import React from 'react';
import { Box, Chip, Divider, Paper, Typography } from '@mui/material';
import { TimelineEvent } from '../../types/trackRecord';
import { fmt } from '../../utils/formatters';

const STRATEGY_NAMES: Record<string, string> = {
  iron_condor: 'Iron Condor',
  bear_call_spread: 'Bear Call Spread',
  bull_put_spread: 'Bull Put Spread',
  long_put_calendar: 'Put Calendar',
  call_calendar: 'Call Calendar',
  put_calendar: 'Put Calendar',
};

function formatStrikes(strikes: Record<string, number | string> | null): string {
  if (!strikes) return '';
  const parts: string[] = [];
  if (strikes.short_put && strikes.long_put) parts.push(`$${strikes.short_put}/$${strikes.long_put} P`);
  if (strikes.short_call && strikes.long_call) parts.push(`$${strikes.short_call}/$${strikes.long_call} C`);
  if (strikes.strike) parts.push(`$${strikes.strike} P`);
  return parts.join(' — ');
}

interface TradeCardProps {
  event: TimelineEvent;
}

const TradeCard: React.FC<TradeCardProps> = ({ event }) => {
  const pnl = event.pnl != null ? Number(event.pnl) : null;
  const roi = event.roi != null ? Number(event.roi) : null;
  const capitalAtRisk = event.capital_at_risk != null ? Number(event.capital_at_risk) : null;
  const price = event.price != null ? Number(event.price) : null;

  // Entry card
  if (event.event_type === 'entry') {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 1.5,
          borderLeft: 4,
          borderColor: 'success.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={event.ticker} size="small" color="primary" sx={{ fontWeight: 700 }} />
          <Chip
            label="OPENED"
            size="small"
            sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 700, letterSpacing: 0.5 }}
          />
          <Typography variant="body2" fontWeight={600}>
            {STRATEGY_NAMES[event.strategy] || event.strategy}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {fmt.dateTime(event.event_time)}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {event.contracts} contracts at {price != null ? `$${price.toFixed(2)} credit` : '—'}
        </Typography>
        {event.strikes && (
          <Typography variant="caption" color="text.secondary">
            {formatStrikes(event.strikes)}
            {event.expiry ? ` · exp ${fmt.shortDate(event.expiry)}` : ''}
          </Typography>
        )}
        {capitalAtRisk != null && (
          <Typography variant="caption" color="text.secondary" display="block">
            Capital at risk: {fmt.currency(capitalAtRisk)}
          </Typography>
        )}
      </Paper>
    );
  }

  // Close card
  if (event.event_type === 'close') {
    const pnlColor = pnl != null ? (pnl >= 0 ? 'success.main' : 'error.main') : 'text.secondary';
    return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 1.5,
          borderLeft: 4,
          borderColor: pnl != null && pnl >= 0 ? 'success.main' : 'error.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={event.ticker} size="small" color="primary" sx={{ fontWeight: 700 }} />
          <Chip
            label="CLOSED"
            size="small"
            sx={{ bgcolor: 'grey.700', color: 'white', fontWeight: 700, letterSpacing: 0.5 }}
          />
          <Typography variant="body2" fontWeight={600}>
            {STRATEGY_NAMES[event.strategy] || event.strategy}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {fmt.dateTime(event.event_time)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {event.contracts} contract{event.contracts !== 1 ? 's' : ''} at {price != null ? `$${price.toFixed(2)} debit` : '—'}
          </Typography>
          {pnl != null && (
            <Typography variant="body2" fontWeight={700} color={pnlColor}>
              {pnl >= 0 ? '+' : ''}{fmt.currency(pnl)}
            </Typography>
          )}
        </Box>
        {event.strikes && (
          <Typography variant="caption" color="text.secondary">
            {formatStrikes(event.strikes)}
            {event.expiry ? ` · exp ${fmt.shortDate(event.expiry)}` : ''}
          </Typography>
        )}
      </Paper>
    );
  }

  // Summary card
  if (event.event_type === 'summary') {
    const pnlColor = pnl != null ? (pnl >= 0 ? 'success.main' : 'error.main') : 'text.secondary';
    const apr = roi != null && event.days_in_trade
      ? (roi / event.days_in_trade) * 365
      : null;

    return (
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 2, sm: 4 },
          alignItems: 'center',
          justifyContent: 'center',
          borderLeft: 4,
          borderColor: pnl != null && pnl >= 0 ? 'success.main' : 'error.main',
        }}
      >
        <Box sx={{ textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            {event.ticker} — {STRATEGY_NAMES[event.strategy] || event.strategy}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            Total G/L
          </Typography>
          <Typography variant="h5" fontWeight={800} color={pnlColor}>
            {pnl != null ? `${pnl >= 0 ? '+' : ''}${fmt.currency(pnl)}` : '—'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            ROI
          </Typography>
          <Typography variant="h5" fontWeight={800} color={roi != null ? pnlColor : 'text.secondary'}>
            {roi != null ? `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%` : '—'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            APR
          </Typography>
          <Typography variant="h5" fontWeight={800} color={apr != null ? pnlColor : 'text.secondary'}>
            {apr != null ? `${apr >= 0 ? '+' : ''}${apr.toFixed(1)}%` : '—'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            Capital at Risk
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {capitalAtRisk != null ? fmt.currency(capitalAtRisk) : '—'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            Days in Trade
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {event.days_in_trade ?? '—'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return null;
};

export default TradeCard;
